import { matchPath } from 'react-router-dom';

import { paths, type AppPathTemplate } from './routePaths';

declare const appUrlBrand: unique symbol;

/**
 * A concrete in-app URL (pathname, optionally followed by `?query` and
 * `#hash`) whose pathname matches a template in `paths`. Only `toAppUrl` and
 * `requireAppUrl` produce one, so `useAppNavigate` can accept it without
 * accepting arbitrary strings.
 */
export type AppUrl = string & { readonly [appUrlBrand]: true };

const appPathTemplates: readonly AppPathTemplate[] = Object.values(paths);

function pathnameOf(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

// Decode each segment the way the router does before matching, so a URL the
// router would mount (e.g. `/user%2Dsettings`) is accepted here too; an
// encoded `/` stays inside its segment.
function decodePathname(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment).replace(/\//g, '%2F');
      } catch {
        return segment;
      }
    })
    .join('/');
}

/** True when `url`'s pathname matches a registered route template. */
function isAppUrl(url: string): url is AppUrl {
  const pathname = decodePathname(pathnameOf(url));
  return appPathTemplates.some(
    (template) => matchPath({ path: template, end: true }, pathname) != null
  );
}

/**
 * Validate a runtime URL that came from data (a staffing-chat navigate event,
 * a backend redirect path, a `?redirect=` query param) before navigating to
 * it. Returns null when its pathname matches no route in `paths`, so the
 * caller decides the fallback.
 *
 * Navigate to a registered route with `nav(paths.X, params, query)` instead;
 * this is only for URLs that already exist as strings.
 */
export function toAppUrl(url: string): AppUrl | null {
  return isAppUrl(url) ? url : null;
}

/**
 * `toAppUrl` for URL strings the app itself produced and that must be
 * registered: `location.pathname` of the mounted page (to rewrite only its
 * query) or a `buildPath(paths.X, …)` result passed around as a string.
 * Throws on an unregistered URL, since that is a bug rather than bad input.
 */
export function requireAppUrl(url: string): AppUrl {
  if (!isAppUrl(url)) {
    throw new Error(
      `requireAppUrl: ${JSON.stringify(url)} matches no route in url-registry paths`
    );
  }
  return url;
}
